import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Input,
  Text,
} from '@chakra-ui/react';
import { CalendarClock, Clock, Moon, Sun } from 'lucide-react';
import { SCHEDULE_LIMITS } from '@twinden/shared';
import {
  defaultScheduleTime,
  localInputValue,
  scheduleTimeError,
  sendLaterChoices,
  type SendLaterChoice,
} from '../../lib/scheduled';
import { appLocale } from '../../lib/format';
import { useT } from '../../i18n';

const ICONS: Record<SendLaterChoice['key'], typeof Clock> = {
  hour: Clock,
  tonight: Moon,
  tomorrow: Sun,
};

const timeOnly = (at: Date) =>
  at.toLocaleTimeString(appLocale(), { hour: 'numeric', minute: '2-digit' });

// Composer "+" → Send later… (M64, D239): quick picks, or a date and time
// 5 minutes to 30 days ahead. Same sheet style as the chat actions (M63).
export function SendLaterSheet({
  isOpen,
  onClose,
  onSchedule,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (at: Date) => void;
}) {
  const t = useT();
  const [opened, setOpened] = useState(() => Date.now());
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    const now = Date.now();
    setOpened(now);
    // An hour on, on a 5-minute mark: a time the sheet will take.
    setValue(localInputValue(defaultScheduleTime(now)));
    setError(null);
  }, [isOpen]);
  const choices = useMemo(() => sendLaterChoices(new Date(opened)), [opened]);
  const label = (choice: SendLaterChoice) =>
    choice.key === 'hour'
      ? t('chat.sendLater.inAnHour')
      : choice.key === 'tonight'
        ? t('chat.sendLater.tonight', { time: timeOnly(choice.at) })
        : t('chat.sendLater.tomorrow', { time: timeOnly(choice.at) });

  const pick = (at: Date) => {
    const problem = scheduleTimeError(at);
    if (problem) {
      setError(problem);
      return;
    }
    onClose();
    onSchedule(at);
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay bg="rgba(31, 29, 27, 0.55)" />
      <DrawerContent
        borderTopRadius="28px"
        bg="surface"
        pb="max(env(safe-area-inset-bottom), 16px)"
        aria-label={t('chat.sendLater.title')}
        maxW={{ md: '480px' }}
        mx={{ md: 'auto' }}
      >
        <DrawerBody px={3} pt="10px" pb={0}>
          <Box
            w="40px"
            h="5px"
            borderRadius="3px"
            bg="gray.200"
            mx="auto"
            mb={2}
            aria-hidden="true"
          />
          <Text
            px={3}
            pt={1}
            fontFamily="heading"
            fontWeight={800}
            fontSize="20px"
          >
            {t('chat.sendLater.title')}
          </Text>
          <Text px={3} pt={1} pb={2} fontSize="15px" color="muted">
            {t('chat.sendLater.body')}
          </Text>
          <Box role="menu" aria-label={t('chat.sendLater.title')}>
            {choices.map((choice) => {
              const Icon = ICONS[choice.key];
              return (
                <Box
                  key={choice.key}
                  as="button"
                  type="button"
                  role="menuitem"
                  w="full"
                  h="56px"
                  px={3}
                  borderRadius="16px"
                  display="flex"
                  alignItems="center"
                  gap="14px"
                  fontSize="17px"
                  fontWeight={600}
                  textAlign="left"
                  color="ink"
                  _hover={{ bg: 'surface.subtle' }}
                  _active={{ bg: 'surface.subtle' }}
                  onClick={() => pick(choice.at)}
                >
                  <Box color="accent" flexShrink={0} aria-hidden="true">
                    <Icon size={20} />
                  </Box>
                  <Text as="span" flex="1">
                    {label(choice)}
                  </Text>
                </Box>
              );
            })}
          </Box>
          <Box px={3} pt={3}>
            <Text
              as="label"
              htmlFor="send-later-time"
              display="flex"
              alignItems="center"
              gap="8px"
              fontSize="15px"
              fontWeight={600}
              color="muted"
              pb={2}
            >
              <CalendarClock size={18} aria-hidden="true" />
              {t('chat.sendLater.pick')}
            </Text>
            <Input
              id="send-later-time"
              type="datetime-local"
              h="52px"
              borderRadius="16px"
              value={value}
              min={localInputValue(
                new Date(opened + SCHEDULE_LIMITS.MIN_LEAD_MS)
              )}
              max={localInputValue(
                new Date(opened + SCHEDULE_LIMITS.MAX_LEAD_MS)
              )}
              onChange={(event) => {
                setValue(event.target.value);
                setError(null);
              }}
            />
            {error && (
              <Text
                role="alert"
                pt={2}
                fontSize="14px"
                color="danger"
                data-testid="send-later-error"
              >
                {error}
              </Text>
            )}
            <Button
              w="full"
              h="52px"
              mt={3}
              colorScheme="brand"
              fontSize="17px"
              onClick={() => pick(new Date(value))}
            >
              {t('chat.sendLater.schedule')}
            </Button>
          </Box>
          <Button
            w="full"
            h="52px"
            mt={2}
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            fontSize="17px"
            onClick={onClose}
          >
            {t('common.close')}
          </Button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
